import { api, type AircraftTypeRow } from '../api';
import { callout, field, input, opt, openDialog, select, val, numVal } from '../dialog';
import { errorCard, esc, html, loading } from '../ui';

/**
 * The aircraft types behind the fleet.
 *
 * Type-level data was write-once: the ICAO designator, the TCDS reference, the
 * mass and the engine count were asked for when a type was created and there
 * was no screen that showed them again, let alone corrected one. A wrong
 * designator is an aircraft the directive check does not recognise, and a wrong
 * mass is a useful load every pilot of the model reads.
 *
 * It is deliberately not a catalogue. This lists only the types the fleet
 * actually flies, because that is the list somebody wants when they have
 * spotted a wrong figure on one of their aeroplanes — and because the record is
 * global, so offering every type on the installation would invite editing a
 * model nobody here has ever seen.
 */

const CATEGORY: Record<string, string> = {
  AEROPLANE: 'Aeroplane',
  HELICOPTER: 'Helicopter',
  SAILPLANE: 'Sailplane',
  BALLOON: 'Balloon',
};

const ELA: Record<string, string> = {
  ELA1: 'ELA1',
  ELA2: 'ELA2',
  NONE: 'Neither',
};

function row(t: AircraftTypeRow): string {
  return html`
    <tr>
      <td class="wide">
        ${esc(t.manufacturer)} ${esc(t.model)}
        <span class="sub2">
          ${esc(CATEGORY[t.category] ?? t.category)}
          ${t.elaClass && t.elaClass !== 'NONE' ? ` · ${esc(t.elaClass)}` : ''}
          ${t.isComplex ? ' · complex motor-powered' : ''}
        </span>
      </td>
      <td class="n">
        ${t.designator ? esc(t.designator) : '<span style="color:var(--caution)">none</span>'}
        <span class="sub2">ICAO</span>
      </td>
      <td class="n">
        ${t.tcdsReference ? esc(t.tcdsReference) : '<span style="color:var(--caution)">none</span>'}
        <span class="sub2">TCDS</span>
      </td>
      <td class="n">
        ${t.mtomKg !== null ? `${t.mtomKg} kg` : '—'}
        <span class="sub2">${t.engineCount} ${t.isTurbine ? 'turbine' : 'piston'}${t.engineCount > 1 ? 's' : ''}</span>
      </td>
      <td>
        ${t.aircraftCount
          ? html`${esc(t.registrations.join(', '))}
              <span class="sub2">${t.aircraftCount} on the fleet${t.archivedCount ? ` · ${t.archivedCount} archived` : ''}</span>`
          : html`<span class="muted">archived only</span>`}
      </td>
      <td style="white-space:nowrap">
        <button class="btn sm" data-type-edit="${esc(t.id)}">Edit</button>
      </td>
    </tr>
  `;
}

/**
 * Correcting a type.
 *
 * The manufacturer and the model are shown and not editable: together they are
 * the type's identity, and renaming one would move every aircraft on it to a
 * different type in every record that names it.
 */
function editType(t: AircraftTypeRow, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: `${t.manufacturer} ${t.model}`,
    sub: t.aircraftCount
      ? `${t.aircraftCount} aircraft on the fleet — ${t.registrations.join(', ')}`
      : 'No aircraft currently on the fleet is of this type',
    primary: 'Save',
    body: () => html`
      ${callout('caution', '!', html`
        <b>A type is shared by every aircraft of the model.</b> These are facts about the type
        certificate, not about one aeroplane — correcting the mass here changes the useful load on
        every aircraft of it. The manufacturer and model are the type's identity and cannot be
        changed; a type entered under the wrong name is a new type, with its aircraft moved to it.
      `)}

      <div class="fset">
        <div class="lg">How it is identified</div>
        <div class="frow">
          ${field('ICAO designator', input('designator', t.designator ?? '', 'style="font-family:var(--mono);text-transform:uppercase" maxlength="8"'),
            'ICAO Doc 8643. What the directive check matches on — a type without one is one the check does not recognise')}
          ${field('TCDS reference', input('tcdsReference', t.tcdsReference ?? '', 'placeholder="EASA.IM.A.081" style="font-family:var(--mono)" maxlength="60"'),
            'The type certificate data sheet the type is approved under')}
        </div>
        <div class="frow">
          ${field('Category', select('category',
            Object.entries(CATEGORY).map(([v, l]) => opt(v, l, v === t.category)).join('')))}
          ${field('ELA class', select('elaClass',
            Object.entries(ELA).map(([v, l]) => opt(v, l, v === (t.elaClass ?? 'NONE'))).join('')),
            'ELA1 and ELA2 are what CS-STAN is written for')}
        </div>
      </div>

      <div class="fset">
        <div class="lg">What decides whether it is complex</div>
        <div class="frow3">
          ${field('MTOM (kg)', input('mtomKg', t.mtomKg ?? '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'),
            'Also what useful load is worked out from')}
          ${field('Max occupants', input('maxOccupants', t.maxOccupants ?? '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Engines', input('engineCount', t.engineCount, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        ${field('Powerplant', select('isTurbine', [
          opt('no', 'Piston', !t.isTurbine),
          opt('yes', 'Turbine', t.isTurbine),
        ].join('')))}
        ${callout('info', 'i', html`
          <b>Complex motor-powered is derived, not chosen.</b> Above 5 700 kg, or more than 19
          occupants, or turbine with more than one engine. It is recomputed when you save, and it is
          what decides whether CS-STAN reaches the aircraft at all — this type is currently
          <b>${t.isComplex ? 'complex' : 'not complex'}</b>.
        `)}
      </div>

      ${field('Reason for the correction',
        input('reason', '', 'placeholder="TCDS read from the certificate; EASA.IM.A.081 not .A.018" maxlength="400"'),
        'Recorded with the before and after')}
    `,
    collect: (root) => {
      const designator = val(root, 'designator').trim().toUpperCase();
      if (!designator) {
        return { error: 'An aircraft type needs its ICAO designator — it is what the directive check matches on.' };
      }
      return {
        designator,
        // Empty clears it rather than leaving the old one in place: a TCDS
        // somebody has deleted because it was wrong should not come back.
        tcdsReference: val(root, 'tcdsReference').trim(),
        category: val(root, 'category'),
        elaClass: val(root, 'elaClass'),
        mtomKg: numVal(root, 'mtomKg'),
        maxOccupants: numVal(root, 'maxOccupants'),
        engineCount: numVal(root, 'engineCount'),
        isTurbine: val(root, 'isTurbine') === 'yes',
        reason: val(root, 'reason').trim() || undefined,
      };
    },
    submit: (payload) => api.aircraft.updateType(t.id, payload),
    done: (r) =>
      r.unchanged
        ? 'Nothing changed'
        : `${r.type} updated — ${r.changed.map((f: string) => TYPE_FIELD[f] ?? f).join(', ')}`,
    onDone: reload,
  });
}

/** What a changed field is called when the toast names it. */
const TYPE_FIELD: Record<string, string> = {
  designator: 'ICAO designator',
  tcdsReference: 'TCDS reference',
  category: 'category',
  elaClass: 'ELA class',
  mtomKg: 'MTOM',
  maxOccupants: 'max occupants',
  engineCount: 'engines',
  isTurbine: 'powerplant',
  isComplex: 'complexity — recomputed',
};

export async function renderTypes(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Aircraft types');

  let types: AircraftTypeRow[];
  try {
    types = (await api.aircraft.types()).types;
  } catch (e) {
    host.innerHTML = errorCard(e instanceof Error ? e.message : 'Could not read the aircraft types.');
    return;
  }

  const incomplete = types.filter((t) => !t.designator || !t.tcdsReference).length;

  const paint = () => {
    host.innerHTML = html`
      <div class="phead">
        <div>
          <h1>Aircraft types</h1>
          <p>
            The type-level record behind every aeroplane on the fleet — what the directive check
            matches on, and what useful load is worked out from. Shared by every aircraft of the
            model.
          </p>
        </div>
      </div>

      ${incomplete
        ? callout('caution', '!', html`
            <b>${incomplete} type${incomplete === 1 ? '' : 's'} ${incomplete === 1 ? 'is' : 'are'}
            missing an ICAO designator or a TCDS reference.</b> A type with no designator is one the
            directive check cannot match, so its aircraft are checked against nothing.
          `)
        : ''}

      <div class="card">
        <div class="card-h">
          <div>
            <h2>Types in use</h2>
            <p class="tiny dim">Only the types your fleet actually flies</p>
          </div>
          <span class="lbl">${types.length} type${types.length === 1 ? '' : 's'}</span>
        </div>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th class="wide">Type</th>
                <th>Designator</th>
                <th>TCDS</th>
                <th>Mass</th>
                <th>On the fleet</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${types.length
                ? types.map(row).join('')
                : '<tr><td colspan="6" class="muted">No aircraft on the fleet yet, so no types to show.</td></tr>'}
            </tbody>
          </table>
        </div>
        <div class="card-f">
          A new type is created from <b>Add aircraft</b>, when the catalogue has not got the one you
          need. Its manufacturer and model cannot be changed afterwards — they are what identifies it.
        </div>
      </div>
    `;

    host.querySelectorAll<HTMLElement>('[data-type-edit]').forEach((b) =>
      b.addEventListener('click', () => {
        const t = types.find((x) => x.id === b.dataset.typeEdit);
        if (t) editType(t, () => void renderTypes(host));
      }),
    );
  };

  paint();
}
